import { useAddBuyerAddress } from '@/hooks/buyers/useAddBuyerAddress';
import { newAddressUiToBuyerAddressRequest } from '@/hooks/buyers/useAddBuyerAddress/mapper';
import { useDeleteBuyerAddress } from '@/hooks/buyers/useDeleteBuyerAddress';
import { useGetBuyerAddresses } from '@/hooks/buyers/useGetBuyerAddresses';
import { getBuyerAddressesResponseToBuyerAddressesUi } from '@/hooks/buyers/useGetBuyerAddresses/mapper';
import { useSetFavoriteAddress } from '@/hooks/buyers/useSetFavoriteAddress';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { DeliveryAddressUi, initialState } from './DeliveryAddressUi';
import { isInvalidAddress, NewAddressUi } from './NewAddress/NewAddressUi';

export function useDeliveryAddressViewController() {
  const [uiState, setUiState] = useImmer<DeliveryAddressUi>(initialState);
  const buyerId = useParams().buyerId;
  const { data } = useGetBuyerAddresses(buyerId);
  const newAddressRef = useRef<NewAddressUi>(undefined);
  const addBuyerAddressHook = useAddBuyerAddress();
  const deleteAddressHook = useDeleteBuyerAddress();
  const setFavoriteAddressHook = useSetFavoriteAddress();

  useEffect(() => {
    if (!data) return;

    setUiState((draft) => {
      draft.deliveryAddresses =
        getBuyerAddressesResponseToBuyerAddressesUi(data);
    });
  }, [data]);

  const onClickedFavorite = (addressId: string) => {
    if (!buyerId) return;
    setFavoriteAddressHook.mutate({ buyerId, addressId });
  };

  const onClickedDelete = (addressId: string) => {
    if (!buyerId) return;
    deleteAddressHook.mutate({ buyerId, addressId });
  };

  const onClickedNewAddress = () =>
    setUiState((draft) => {
      draft.addingNewAddress = true;
    });

  const onAddressChanged = (newAddress: NewAddressUi) => {
    if (isInvalidAddress(newAddress) && uiState.saveDisabled) return;
    newAddressRef.current = newAddress;
    setUiState((draft) => {
      draft.saveDisabled = isInvalidAddress(newAddress);
    });
  };

  const cancelNewAddress = () =>
    setUiState((draft) => {
      draft.addingNewAddress = false;
      draft.saveDisabled = true;
    });

  const onClickedCancel = () => cancelNewAddress();

  const onClickedSave = () => {
    if (!newAddressRef.current || !buyerId) return;
    const buyerAddressRequest = newAddressUiToBuyerAddressRequest(
      newAddressRef.current
    );
    addBuyerAddressHook.mutate(
      { buyerId, buyerAddressRequest },
      {
        onSuccess: () => cancelNewAddress(),
      }
    );
  };

  return {
    uiState,
    actions: {
      onClickedFavorite,
      onClickedDelete,
      onClickedNewAddress,
      onAddressChanged,
      onClickedCancel,
      onClickedSave,
    },
  };
}
